import type { FC } from "preact/compat";
import classnames from "classnames";
import Icons from "@components/icons.ts";
import { Filters } from "../Filters";
import { ComboboxGroupedButtons } from "@components/ComboBoxes/ComboboxButtonGroup";
import { ComboboxButtonWrapper } from "@components/ComboBoxes/ComboboxButtonWrapper";
import { ComboboxButtonMultiSelect } from "@components/ComboBoxes/ComboboxButtonMultiSelect";
import { ComboboxGroupButton } from "@components/ComboBoxes/ComboboxButton";
import type {
  Payors,
  Specialties,
  States,
} from "@services/filter-api/types";
import { useEffect, useState } from "preact/hooks";
import "./index.scss";

export const FilterComponentGroup: FC<{
  states: States;
  specialties: Specialties,
  optional_filter_props?: Array<"state" | "needs" | "insurance">;
  insurance: Payors
  filter_buttons_props?: Array<{
    name: string;
    label: string;
    is_optional: boolean;
    is_multi_select: boolean;
    placeholder: string;
  }>;
  isMobile?: boolean;
  new_styles?: boolean;
  tabbed_filters?: boolean;
  type_of_care?: string;
  type_of_care_filter_tabs?: Array<{
    icon: string;
    label: string;
    parameter_value: string;
    short_label?: string;
  }>;
}> = ({
  specialties: specialty,
  states: statesFilters,
  insurance,
  optional_filter_props,
  filter_buttons_props,
  new_styles,
  tabbed_filters,
  type_of_care,
  type_of_care_filter_tabs,
  isMobile: propIsMobile,
}) => {
  const [typeOfCare, setTypeOfCare] = useState<string | undefined>(type_of_care);
  const [iconComponents, setIconComponents] = useState<Record<string, any>>({});

  // Handles tab click events
  useEffect(() => {
    if (!tabbed_filters) return;

    const container = document.querySelector('[data-combobox-tab-group]');
    if (!container) return;

    const handleTabClick = (e: Event) => {
      const target = e.target as Element;
      const clickedTab = target?.closest('[data-combobox-tab-group-tab]') as HTMLElement;
      if (!clickedTab) return;

      container.querySelectorAll('[data-combobox-tab-group-tab]').forEach(tab => {
        tab.classList.remove('combobox-tab-group__tab--active');
      })

      clickedTab.classList.add('combobox-tab-group__tab--active');

      setTypeOfCare(clickedTab.dataset.comboboxTabGroupTab);
    };

    container.addEventListener('click', handleTabClick);
    return () => container.removeEventListener('click', () => {});
  }, []);

  // Handles fetching of icons that are used in the tabs
  useEffect(() => {
    const loadIcons = async () => {
      const icons: Record<string, any> = {};
      if (type_of_care_filter_tabs) {
        for (const tab of type_of_care_filter_tabs) {
          if (!icons[tab.icon]) {
            try {
              const module = await import(`../../../icons/${tab.icon}-icon.tsx`);
              icons[tab.icon] = module.default;
            } catch (error) {
              icons[tab.icon] = null;
            }
          }
        }
      }
      setIconComponents(icons);
    };

    loadIcons();
  }, [type_of_care_filter_tabs]);

  // Helper function to render icon
  const renderIcon = (iconName: string) => {
    const IconComponent = iconComponents[iconName];
    return IconComponent ? <IconComponent /> : null;
  };

  return (
    <Filters
      specialties={specialty}
      states={statesFilters}
      insurance={insurance}
      optional_filter_props={optional_filter_props}
      filter_buttons_props={filter_buttons_props}
      type_of_care={typeOfCare}
    >
      {({
        selectedItems,
        handleRedirectClick,
        selectLater,
        handleSelectLater,
        isMobile: filterIsMobile,
        insurance,
        specialty,
        statesFilters,
        handleOpenStates,
        handleOpenInsurance,
        handleOpenNeeds,
        handleGetSelectedInsurance,
        handleGetSelectedState,
        openInsurance,
        openNeeds,
        openStates,
        handleGetSelectedNeeds,
        errors,
      }) => {
        const isMobile = propIsMobile ?? filterIsMobile;

        return (
          <div
            className={classnames(
            "filter-component-group relative w-full md:w-fit", {
              "filter-component-group--tabbed-filters": tabbed_filters,
            })}
          >
            {tabbed_filters && type_of_care_filter_tabs && (
              <div className="combobox-tab-group" data-combobox-tab-group>
                {type_of_care_filter_tabs.map(tab => (
                  <button
                    className={`combobox-tab-group__tab ${type_of_care === tab.parameter_value ? 'combobox-tab-group__tab--active' : ''}`}
                    type="button"
                    data-combobox-tab-group-tab={tab.parameter_value}
                  >
                    {renderIcon(tab.icon)}
                    <span className="combobox-tab-group__label--short">
                      {tab.short_label !== '' ? tab.short_label : tab.label}
                    </span>
                    <span className="combobox-tab-group__label">
                      {tab.label}
                    </span>
                  </button>
                ))}
              </div>
            )}
            <ComboboxGroupedButtons
              new_styles={new_styles}
              tabbed_filters={tabbed_filters}
            >
              <ComboboxButtonWrapper
                optional={filter_buttons_props?.[0]?.is_optional}
                handleOpenClick={handleOpenStates}
                buttonWrapperClass={classnames(
                  "w-11/12 md:rounded-s-lg border-b md:border my-2 md:my-0",
                  {
                    "border-[#B35436]": errors?.state,
                    "border-gt-black": !errors?.state,
                  },
                )}
                open={openStates}
                error={errors?.state}
                selectedItem={selectedItems.state}
                isMobile={isMobile}
                endIcon={<Icons.CaretDownIcon open={openStates} />}
                data={
                  selectedItems.insurance
                    ? statesFilters.map((_state) => {
                        const states = insurance.find(
                          (_insurance) =>
                            _insurance.value === selectedItems.insurance?.value,
                        )?.states;

                        const hasStates = states?.find(
                          (_s) =>
                            _s.abbreviation?.toLowerCase() ===
                            _state.abbreviation?.toLowerCase(),
                        );
                        if (hasStates) {
                          return {
                            ..._state,
                            filtered: true,
                          };
                        }
                        return _state;
                      })
                    : statesFilters
                }
                placeholder={
                  filter_buttons_props?.[0]
                    ? `${filter_buttons_props[0].placeholder}`
                    : `Select your state`
                }
                label={
                  filter_buttons_props?.[0]
                    ? `${filter_buttons_props[0].label}${filter_buttons_props[0].is_optional ? "" : "*"}`
                    : `location`
                }
                handleGetSelectedItem={handleGetSelectedState}
              />

              <ComboboxButtonWrapper
                optional={filter_buttons_props?.[1]?.is_optional}
                handleOpenClick={handleOpenInsurance}
                open={openInsurance}
                error={errors?.insurance}
                buttonWrapperClass={classnames(
                  "w-11/12 md:border-t border-b md:border-r my-2 md:my-0",
                  {
                    "border-[#B35436]": errors?.insurance,
                    "border-gt-black": !errors?.insurance,
                  },
                )}
                selectedItem={selectedItems.insurance}
                isMobile={isMobile}
                endIcon={<Icons.CaretDownIcon open={openInsurance} />}
                data={
                  // find insurance with the selected state
                  selectedItems.state
                    ? insurance.map((_insurance) => {
                        const insurerHasSelectedState = _insurance.states?.find(
                          (_s) =>
                            _s.abbreviation?.toLowerCase() ===
                            selectedItems.state?.abbreviation?.toLowerCase(),
                        );

                        if (insurerHasSelectedState) {
                          return {
                            ..._insurance,
                            filtered: true,
                          };
                        }
                        return _insurance;
                      })
                    : insurance
                }
                placeholder={
                  filter_buttons_props?.[1]
                    ? `${filter_buttons_props[1].placeholder}`
                    : `Select your carrier`
                }
                label={
                  filter_buttons_props?.[1]
                    ? `${filter_buttons_props[1].label}${filter_buttons_props[1].is_optional ? "" : "*"}`
                    : `insurance`
                }
                handleGetSelectedItem={handleGetSelectedInsurance}
              />
              <ComboboxButtonMultiSelect
                isMobile={isMobile}
                handleOpenClick={handleOpenNeeds}
                buttonWrapperClass='border-gt-black w-11/12 md:border-t border-b md:border-r my-2 md:my-0'
                open={openNeeds}
                specialties={specialty}
                endIcon={<Icons.CaretDownIcon open={openNeeds} />}
                placeholder={
                  filter_buttons_props?.[2]
                    ? `${filter_buttons_props[2].placeholder}`
                    : `Select your needs`
                }
                label={
                  filter_buttons_props?.[2]
                    ? `${filter_buttons_props[2].label}${filter_buttons_props[2].is_optional ? "" : "*"}`
                    : `needs`
                }
                optional={filter_buttons_props?.[2]?.is_optional}
                handleGetSelectedItems={handleGetSelectedNeeds}
                selectedItems={selectedItems?.specialty}
                selectLater={selectLater}
                handleSelectLater={handleSelectLater}
              />

              <ComboboxGroupButton
                variant='contained'
                button='submit'
                buttonWrapperClass='border-gt-black w-11/12 rounded-e-lg md:border-t md:border-b md:border-r md:border-l-0 my-4 md:my-0'
                label={
                  <div className='flex items-center w-full justify-center'>
                    <Icons.SearchIcon />
                    <span className='ComboboxButton__search-text ml-1'>Search</span>
                  </div>
                }
                onClick={handleRedirectClick}
              />
            </ComboboxGroupedButtons>
          </div>
        );
      }}
    </Filters>
  );
};
